// Due-date helpers for the dashboard's project banner (pages/dashboard.js, TPT434): which of a
// project's open tasks are due, in what order, and how to caption them — plus the task detail
// Due date field's state, input parsing and display (components/task-detail.js, TPT589). Pure
// (no DOM, no api.js) so node:test can import it without stubs — same shape as
// lib/relative-time.js.
//
// Dates are the API's calendar-day strings (`YYYY-MM-DD`, tasks.due_date) compared against the
// viewer's LOCAL calendar day, the same rule utils.js renderDuePill() uses for its overdue pill.

const DAY_MS = 86400000;
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Midnight-UTC timestamp of a `YYYY-MM-DD` string, or null when it isn't one. UTC only so two
// days subtract to a whole number of days regardless of DST.
function dayStamp(iso) {
  const m = typeof iso === 'string' ? ISO_DAY.exec(iso) : null;
  if (!m) return null;
  const stamp = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(stamp) ? stamp : null;
}

// The viewer's local calendar day as `YYYY-MM-DD`.
export function todayIso(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// Whole days from `today` to `dueDate` (negative = overdue), or null for a malformed date.
export function dueDayDiff(dueDate, today = todayIso()) {
  const due = dayStamp(dueDate);
  const base = dayStamp(today);
  if (due == null || base == null) return null;
  return Math.round((due - base) / DAY_MS);
}

// "Overdue 3d" / "Today" / "Tomorrow" / "in 5d" (up to a week out) / "Oct 12" beyond that.
export function dueLabel(diff, dueDate) {
  if (diff == null) return '';
  if (diff < 0) return `Overdue ${-diff}d`;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff <= 7) return `in ${diff}d`;
  const m = ISO_DAY.exec(dueDate || '');
  return m ? `${MONTHS[Number(m[2]) - 1] || ''} ${Number(m[3])}`.trim() : '';
}

// "Oct 12" for a `YYYY-MM-DD` string, '' when it isn't one.
export function shortDay(iso) {
  const m = typeof iso === 'string' ? ISO_DAY.exec(iso) : null;
  const month = m && MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}` : '';
}

// "Oct 12, 2026" for a `YYYY-MM-DD` string, '' when it isn't one.
export function formatDay(iso) {
  const day = shortDay(iso);
  return day ? `${day}, ${iso.slice(0, 4)}` : '';
}

// An `<input type="date">` value as a calendar day to save: null for an emptied field (clears the
// date), the `YYYY-MM-DD` string for a complete one, undefined for anything else. Chrome fires
// `change` on every keystroke that completes a date, so typing the year 2026 passes through
// `0002-…`, `0020-…` and `0202-…` first; the four-digit, non-zero-leading year rule (the sprint
// API's own) skips those.
export function parseDayInput(raw) {
  if (raw == null || raw === '') return null;
  return typeof raw === 'string' && /^[1-9]\d{3}-\d{2}-\d{2}$/.test(raw) && dayStamp(raw) != null ? raw : undefined;
}

// The task detail Due date field's state pill: null for a task with no valid due date or whose
// status is in `closed` (a finished task is never overdue), else the whole days left (negative =
// overdue) and the caption.
export function taskDueState(task, closed, today = todayIso()) {
  const closedSet = closed instanceof Set ? closed : new Set(closed || []);
  if (!task || closedSet.has(task.status)) return null;
  const diff = dueDayDiff(task.due_date, today);
  if (diff == null) return null;
  let label;
  if (diff < 0) label = `Overdue ${-diff}d`;
  else if (diff === 0) label = 'Due today';
  else if (diff === 1) label = 'Due tomorrow';
  else label = `Due in ${diff}d`;
  return { diff, overdue: diff < 0, label };
}

// Open tasks carrying a due date, soonest first (so overdue ones lead). `closed` is the set of
// status names that mean done — pass lib/statuses.js closedNames() of the owning project.
// Returns the first `limit` rows (each `{ task, diff, overdue }`) plus the totals behind them:
// `total` dated open tasks and how many of those are `overdue`.
export function selectDueTasks(tasks, { closed, today = todayIso(), limit = 3 } = {}) {
  const closedSet = closed instanceof Set ? closed : new Set(closed || []);
  const dated = [];
  for (const task of tasks || []) {
    if (!task || task.is_reservation || closedSet.has(task.status)) continue;
    const diff = dueDayDiff(task.due_date, today);
    if (diff == null) continue;
    dated.push({ task, diff, overdue: diff < 0 });
  }
  dated.sort((a, b) => a.diff - b.diff);
  return {
    rows: dated.slice(0, Math.max(0, limit)),
    total: dated.length,
    overdue: dated.filter(r => r.overdue).length,
  };
}
